'use client'

// Root-level error boundary. Catches errors that crash the entire root layout
// (including the <html> tag itself) so we never serve a totally blank page.
// Per Next.js convention this MUST include <html>/<body> because it replaces
// the whole document — which is why everything here is inline styles and
// system fonts: globals.css and the Solis tokens may never have loaded.
//
// Deliberately simpler than app/error.tsx: no Claude section, no crash report
// POST (the app shell just failed — the fewer moving parts the better).
// Sentry's browser SDK is still explicitly told, and the support email is
// prefilled with the reference so the biller never retypes it.

import { useEffect, useRef } from 'react'
import * as Sentry from '@sentry/nextjs'
import { supportMailtoHref } from '@/lib/errors/support-email'

const GLOBAL_TECHNICAL_NOTE = 'The root layout crashed (global-error boundary).'

export default function GlobalError({
  error,
  reset,
  ...rest
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const mailtoRef = useRef<HTMLAnchorElement>(null)

  // The rendered href is deterministic (no Date, no window — hydration-safe);
  // page + time are enriched imperatively after mount, no re-render needed.
  const mailto = supportMailtoHref({
    digest: error.digest ?? null,
    occurrenceId: null,
    page: null,
    atIso: '',
    plain: null,
    technical: GLOBAL_TECHNICAL_NOTE,
  })

  useEffect(() => {
    Sentry.captureException(error)
    if (mailtoRef.current) {
      mailtoRef.current.href = supportMailtoHref({
        digest: error.digest ?? null,
        occurrenceId: null,
        page: window.location.pathname,
        atIso: new Date().toISOString(),
        plain: null,
        technical: GLOBAL_TECHNICAL_NOTE,
      })
    }
  }, [error])

  // Next 16.2 also passes unstable_retry (re-fetches instead of re-rendering);
  // read from rest — the serializable-props lint doesn't know the prop yet.
  const retry = (rest as { unstable_retry?: () => void }).unstable_retry ?? reset

  return (
    <html lang="en">
      <body style={{
        margin: 0,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        background: '#0f172a',
        color: '#f8fafc',
      }}>
        <div style={{
          maxWidth: 520,
          padding: '32px',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: 12,
            fontFamily: 'ui-monospace, monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            opacity: 0.6,
            marginBottom: 14,
          }}>
            Sunbound RCM · Remark code: OOPS-00
          </p>
          <h1 style={{ fontSize: 28, fontWeight: 500, margin: '0 0 12px' }}>
            Something went wrong.
          </h1>
          <p style={{ fontSize: 15, opacity: 0.7, marginBottom: 24, lineHeight: 1.5 }}>
            It broke badly enough that even the nice error page couldn&apos;t load.
            Try again, and if it sticks, email us — we&apos;ll take it from there.
          </p>
          {error.digest && (
            <p style={{ fontSize: 12, opacity: 0.4, fontFamily: 'ui-monospace, monospace', marginBottom: 24 }}>
              Reference: {error.digest}
            </p>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={retry}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#3b82f6',
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            {/* Deliberately a hard <a>, not <Link>: global-error means the app
                shell (router included) just crashed — a full document load is
                the only navigation guaranteed to work here. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'transparent',
                color: '#f8fafc',
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Go home
            </a>
            <a
              ref={mailtoRef}
              href={mailto}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'transparent',
                color: '#f8fafc',
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Email support
            </a>
          </div>
        </div>
      </body>
    </html>
  )
}
